import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, User as UserIcon, Mail, Phone, Loader2, Users, Upload, FileSpreadsheet, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import NumberInput from './shared/NumberInput';
import PhoneInput from './shared/PhoneInput';
import { formatPhone } from './shared/phone';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { uc, mergeQueue, CONTACT_TYPES, LEAD_STATUSES } from './unifiedContactsApi';
import { api } from './api';
import { useAuth } from './AuthContext';
import { errorMessage } from './errors';
import useOpenNewFromUrl from './kit/useOpenNewFromUrl';

const EMPTY = {
  first_name: '', last_name: '', preferred_name: '',
  contact_type: 'lead', lead_status: 'new', credit_score: '',
  emails: [{ value: '', is_primary: true }],
  phones: [{ value: '', is_primary: true }],
  preferred_language: 'en',
  communication_preference: 'any',
  tags_csv: '',
  notes: '',
};

const PAGE_SIZES = [20, 50, 100];
const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];
const SEARCH_DEBOUNCE_MS = 300;
const MAX_PAGE = 1000000; // keeps skip inside what the API accepts

export default function UnifiedContactsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canMerge = user?.is_super_admin || (user?.permissions || []).includes('contacts.merge');
  const [needsReview, setNeedsReview] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ticket 150: filters + page live in the URL so reload and Back restore them.
  // Defaults are omitted, so a clean URL is /portal/contacts.
  const [sp, setSp] = useSearchParams();
  const q = sp.get('q') || '';
  const contactType = sp.get('type') || '';
  const leadStatus = sp.get('status') || '';
  const rawPage = parseInt(sp.get('page'), 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1 ? Math.min(rawPage, MAX_PAGE) : 1;
  const rawSize = parseInt(sp.get('size'), 10);
  const pageSize = PAGE_SIZES.includes(rawSize) ? rawSize : DEFAULT_PAGE_SIZE;
  const [qInput, setQInput] = useState(q);
  const lastQ = useRef(q);
  const listTop = useRef(null);
  const listScroll = useRef(null);
  const prevPage = useRef(page);
  const reqId = useRef(0);
  const filtersActive = Boolean(q || contactType || leadStatus);
  const [creating, setCreating] = useState(false);
  useOpenNewFromUrl(() => setCreating(true)); // the top-bar + menu links here with ?new=1
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  // Merge `patch` into the query string. '' / null / a default page or page
  // size drop the key. Filter and size changes reset the page unless the patch
  // sets one.
  const setParams = useCallback((patch, { replace = false } = {}) => {
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      const merged = 'page' in patch ? patch : { ...patch, page: 1 };
      Object.entries(merged).forEach(([k, v]) => {
        if (v === '' || v == null || (k === 'page' && Number(v) <= 1) || (k === 'size' && Number(v) === DEFAULT_PAGE_SIZE)) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace });
  }, [setSp]);

  // Search box: local state, debounced into the URL (replace, so typing does
  // not fill history). Re-sync when q changes from outside (Back, chip x, Reset).
  useEffect(() => {
    if (q !== lastQ.current) {
      lastQ.current = q;
      setQInput(q);
    }
  }, [q]);

  useEffect(() => {
    const next = qInput.trim();
    if (next === lastQ.current) return undefined;
    const t = setTimeout(() => {
      // A new search pushes history (Back returns to the page you were on);
      // refining an existing q replaces, so typing doesn't stack entries.
      const refining = lastQ.current !== '';
      lastQ.current = next;
      setParams({ q: next }, { replace: refining });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qInput, setParams]);

  const load = useCallback(async () => {
    const id = ++reqId.current;
    const isStale = () => id !== reqId.current;
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: (page - 1) * pageSize };
      if (q) params.q = q;
      if (contactType) params.contact_type = contactType;
      if (leadStatus) params.lead_status = leadStatus;
      const data = await uc.list(params);
      if (isStale()) return;
      const t = data.total || 0;
      setItems(data.items || []);
      setTotal(t);
      const lastPage = Math.max(1, Math.ceil(t / pageSize));
      if (t > 0 && page > lastPage) setParams({ page: lastPage }, { replace: true });
    } catch (e) {
      if (isStale()) return;
      toast({ title: 'Failed to load contacts', description: errorMessage(e) });
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [q, contactType, leadStatus, page, pageSize, setParams]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (prevPage.current !== page) {
      prevPage.current = page;
      // Sticky top nav is 57px (h-14 + border); scroll only if the list top is under/above it.
      const top = listTop.current?.getBoundingClientRect?.().top;
      if (top === undefined || top < 57) {
        listTop.current?.scrollIntoView?.({ block: 'start' });
      }
      if (listScroll.current) listScroll.current.scrollTop = 0;
    }
  }, [page]);

  useEffect(() => {
    if (!canMerge) return;
    mergeQueue.summary().then(setNeedsReview).catch(() => null);
  }, [canMerge]);

  const handleCreate = async () => {
    if (!form.first_name && !form.last_name && !form.emails[0]?.value && !form.phones[0]?.value) {
      toast({ title: 'Please provide at least a name, email, or phone' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        preferred_name: form.preferred_name || null,
        contact_type: form.contact_type,
        // Ticket 75: lead status only means something for leads; null = n/a.
        lead_status: form.contact_type === 'lead' ? form.lead_status : null,
        credit_score: form.credit_score ? Number(form.credit_score) : null,
        preferred_language: form.preferred_language,
        communication_preference: form.communication_preference,
        emails: form.emails.filter(e => e.value),
        phones: form.phones.filter(p => p.value),
        tags: form.tags_csv ? form.tags_csv.split(',').map(t => t.trim()).filter(Boolean) : [],
        notes: form.notes || null,
      };
      const created = await uc.create(payload);
      toast({ title: 'Contact created', description: created.display_name });
      setCreating(false);
      setForm(EMPTY);
      nav(`/portal/contacts/${created.contact_id}`);
    } catch (e) {
      toast({ title: 'Create failed', description: e?.response?.data?.detail || String(e) });
    } finally {
      setSaving(false);
    }
  };

  const label = (arr, k) => (arr.find(x => x.k === k)?.l) || k;
  const resetFilters = () => setParams({ q: '', type: '', status: '' });
  const lastPage = Math.max(1, Math.ceil((total || 0) / pageSize));
  const rangeStart = (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total || 0);
  // Past the last page the redirect to it is pending: show neither the empty
  // state nor the paging bar meanwhile.
  const outOfRange = total > 0 && page > lastPage;
  const chips = [
    q && { key: 'q', text: `Search: ${q}`, name: 'Search', clear: { q: '' } },
    contactType && { key: 'type', text: `Type: ${label(CONTACT_TYPES, contactType)}`, name: 'Type', clear: { type: '' } },
    leadStatus && { key: 'status', text: `Lead status: ${label(LEAD_STATUSES, leadStatus)}`, name: 'Lead status', clear: { status: '' } },
  ].filter(Boolean);

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="unified-contacts-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }} data-testid="contacts-title">Contacts</h1>
          <p className="text-slate-500 mt-1" data-testid="contacts-subtitle">Unified people & household records — mortgage and real estate</p>
          {total !== null && (
            <p className="text-sm font-medium text-slate-700 mt-1" data-testid="contacts-total-count">
              {filtersActive
                ? `${total.toLocaleString()} matching`
                : `${total.toLocaleString()} ${total === 1 ? 'contact' : 'contacts'}`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap md:flex-nowrap items-center gap-2">
          {canMerge && (
            <Link to="/portal/contacts/review" className="btn-outline" data-testid="needs-review-btn">
              Needs review{needsReview?.open ? ` · ${needsReview.open}` : ''}
            </Link>
          )}
          <button
            onClick={() => setImporting(true)}
            className="btn-outline"
            data-testid="import-csv-btn"
          >
            <Upload size={16} /> Import CSV
          </button>
          <button
            onClick={() => setCreating(true)}
            className="btn-gold"
            data-testid="new-contact-btn"
          >
            <Plus size={16} /> New Contact
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <Label className="text-xs text-slate-500">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Name, email, or phone..."
              className="pl-9"
              data-testid="contact-search-input"
            />
          </div>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs text-slate-500">Type</Label>
          <Select value={contactType || 'all'} onValueChange={(v) => setParams({ type: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="contact-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {CONTACT_TYPES.map(t => <SelectItem key={t.k} value={t.k}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs text-slate-500">Lead Status</Label>
          <Select value={leadStatus || 'all'} onValueChange={(v) => setParams({ status: v === 'all' ? '' : v })}>
            <SelectTrigger data-testid="lead-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUSES.map(s => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="contacts-filter-chips">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex items-center gap-1 max-w-full rounded-full bg-slate-100 pl-3 pr-1 py-1 text-xs text-slate-700"
              data-testid={`filter-chip-${c.key}`}
            >
              <span className="truncate">{c.text}</span>
              <button
                type="button"
                onClick={() => setParams(c.clear)}
                className="shrink-0 rounded-full p-1 hover:bg-slate-200"
                aria-label={`Remove ${c.name} filter`}
                data-testid={`filter-chip-remove-${c.key}`}
              >
                <X size={12} />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs font-medium underline px-1"
            style={{ color: '#070B5F' }}
            data-testid="contacts-reset-filters"
          >
            Reset filters
          </button>
        </div>
      )}

      {/* List */}
      <div ref={listTop} className="scroll-mt-20 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
        {loading || outOfRange ? (
          <div className="p-16 flex items-center justify-center text-slate-400">
            <Loader2 className="animate-spin mr-2" size={20} /> Loading contacts...
          </div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-slate-500" data-testid="contacts-empty-state">
            <Users size={40} className="mx-auto mb-3 text-slate-300" />
            <p className="font-medium">No contacts found</p>
            <p className="text-sm mt-1">Adjust your filters or create your first contact.</p>
          </div>
        ) : (
          // Only the rows scroll; the paging bar below stays in view (ticket 188).
          <div ref={listScroll} className="max-h-[calc(100vh-22rem)] min-h-[16rem] overflow-y-auto" data-testid="contacts-list-scroll">
            <ul className="divide-y divide-slate-100">
              {items.map((c) => (
                <li key={c.contact_id}>
                  <Link
                    to={`/portal/contacts/${c.contact_id}`}
                    className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4 hover:bg-slate-50 transition"
                    data-testid={`contact-row-${c.contact_id}`}
                  >
                    <div className="w-10 h-10 shrink-0 rounded-full flex items-center justify-center" style={{ background: '#070B5F', color: '#A6E56C' }}>
                      <UserIcon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate" style={{ color: '#070B5F' }}>{c.display_name}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-0.5">
                        {c.emails?.[0]?.value && (
                          <span className="flex items-center gap-1 min-w-0 max-w-full"><Mail size={12} className="shrink-0" /><span className="truncate">{c.emails[0].value}</span></span>
                        )}
                        {c.phones?.[0]?.value && (
                          <span className="flex items-center gap-1"><Phone size={12} />{formatPhone(c.phones[0].value)}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{label(CONTACT_TYPES, c.contact_type)}</span>
                      {c.contact_type === 'lead' && c.lead_status && (
                        <span className="text-[10px] text-slate-400">{label(LEAD_STATUSES, c.lead_status)}</span>
                      )}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
        {total > 0 && !outOfRange && (
          <div
            className="px-4 sm:px-5 py-3 border-t border-slate-100 bg-white text-xs text-slate-500 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
            data-testid="contacts-paging-bar"
          >
            <span data-testid="contacts-page-range">
              Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of {total.toLocaleString()}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={String(pageSize)} onValueChange={(v) => setParams({ size: v })}>
                <SelectTrigger className="h-8 w-[110px] text-xs" aria-label="Rows per page" data-testid="contacts-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map(n => <SelectItem key={n} value={String(n)}>{n} per page</SelectItem>)}
                </SelectContent>
              </Select>
              <button
                type="button"
                className="btn-outline"
                disabled={page <= 1 || loading}
                onClick={() => setParams({ page: page - 1 })}
                data-testid="contacts-page-prev"
              >
                Prev
              </button>
              <span data-testid="contacts-page-indicator">Page {page.toLocaleString()} of {lastPage.toLocaleString()}</span>
              <button
                type="button"
                className="btn-outline"
                disabled={page >= lastPage || loading}
                onClick={() => setParams({ page: page + 1 })}
                data-testid="contacts-page-next"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">New Contact</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="contact-first-name" />
            </div>
            <div>
              <Label>Last Name</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="contact-last-name" />
            </div>
            <div>
              <Label>Preferred Name</Label>
              <Input value={form.preferred_name} onChange={(e) => setForm({ ...form, preferred_name: e.target.value })} />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.contact_type} onValueChange={(v) => setForm({ ...form, contact_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONTACT_TYPES.map(t => <SelectItem key={t.k} value={t.k}>{t.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={form.emails[0]?.value || ''}
                onChange={(e) => setForm({ ...form, emails: [{ value: e.target.value, is_primary: true }] })}
                data-testid="contact-email"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <PhoneInput
                value={form.phones[0]?.value || ''}
                onChange={(d) => setForm({ ...form, phones: [{ value: d, is_primary: true }] })}
                data-testid="contact-phone"
              />
            </div>
            <div>
              <Label>Communication Preference</Label>
              <Select value={form.communication_preference} onValueChange={(v) => setForm({ ...form, communication_preference: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.contact_type === 'lead' && (
              <div data-testid="contact-lead-status-field">
                <Label>Lead Status</Label>
                <Select value={form.lead_status} onValueChange={(v) => setForm({ ...form, lead_status: v })}>
                  <SelectTrigger data-testid="contact-lead-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STATUSES.map(s => <SelectItem key={s.k} value={s.k}>{s.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Credit Score</Label>
              <NumberInput integer min={300} max={850} value={form.credit_score}
                onChange={(n) => setForm({ ...form, credit_score: n })} data-testid="contact-credit-score" />
            </div>
            <div className="col-span-2">
              <Label>Tags (comma-separated)</Label>
              <Input value={form.tags_csv} onChange={(e) => setForm({ ...form, tags_csv: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-outline" onClick={() => setCreating(false)}>Cancel</button>
            <button
              className="btn-gold"
              disabled={saving}
              onClick={handleCreate}
              data-testid="contact-save-btn"
            >
              {saving && <Loader2 className="animate-spin" size={14} />}
              Save Contact
            </button>
          </div>
        </DialogContent>
      </Dialog>
      {/* CSV Import Modal */}
      <Dialog open={importing} onOpenChange={(v) => { setImporting(v); if (!v) { setImportPreview(null); setImportResult(null); setImportFile(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="import-modal">
          <DialogHeader><DialogTitle style={{ color: '#070B5F' }} className="font-serif">Import Contacts from CSV</DialogTitle></DialogHeader>
          {!importPreview && !importResult && (
            <div className="space-y-3">
              <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
                <FileSpreadsheet size={40} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm text-slate-600 mb-3">Upload a .csv file — we'll auto-detect columns.</p>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setImportFile(f);
                    const fd = new FormData();
                    fd.append('file', f);
                    try {
                      const { data } = await api.post('/v2/contacts/import/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setImportPreview(data);
                    } catch (err) {
                      toast({ title: 'Preview failed', description: err?.response?.data?.detail || String(err) });
                    }
                  }}
                  data-testid="csv-file-input"
                />
              </div>
              <p className="text-xs text-slate-400">Supported columns: first_name, last_name, email, phone, tags, notes, contact_type. Duplicate detection uses normalized email + phone.</p>
            </div>
          )}
          {importPreview && !importResult && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-slate-700 mb-2">Detected {importPreview.headers.length} columns</p>
                <div className="text-xs text-slate-500 grid grid-cols-2 gap-1">
                  {importPreview.headers.map(h => (
                    <div key={h} className="flex items-center justify-between border border-slate-100 rounded px-2 py-1">
                      <span className="text-slate-700 truncate">{h}</span>
                      <span className="text-slate-400 text-[10px]">→ {importPreview.suggested_mapping[h] || 'ignored'}</span>
                    </div>
                  ))}
                </div>
              </div>
              {importPreview.sample.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">Sample rows</p>
                  <div className="text-[11px] bg-slate-50 rounded-lg p-2 overflow-x-auto">
                    <pre>{JSON.stringify(importPreview.sample.slice(0, 3), null, 2)}</pre>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => { setImportPreview(null); setImportFile(null); }} className="btn-outline">Back</button>
                <button data-testid="csv-import-confirm"
                  onClick={async () => {
                    if (!importFile) return;
                    const fd = new FormData();
                    fd.append('file', importFile);
                    fd.append('mapping_json', JSON.stringify(importPreview.suggested_mapping));
                    fd.append('dedupe', 'true');
                    try {
                      const { data } = await api.post('/v2/contacts/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setImportResult(data);
                      load();
                    } catch (e) {
                      toast({ title: 'Import failed', description: e?.response?.data?.detail || String(e) });
                    }
                  }}
                  className="btn-gold">
                  Import
                </button>
              </div>
            </div>
          )}
          {importResult && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800">
                ✓ Imported {importResult.inserted} contacts.
                {importResult.skipped_duplicates > 0 && <> Skipped {importResult.skipped_duplicates} duplicate{importResult.skipped_duplicates > 1 ? 's' : ''}.</>}
                {(importResult.errors?.length || 0) > 0 && <div className="text-red-500 mt-2">Errors: {importResult.errors.length}</div>}
              </div>
              <div className="flex justify-end">
                <button onClick={() => { setImporting(false); setImportPreview(null); setImportResult(null); setImportFile(null); }}
                  className="btn-gold">Done</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
